import { useEffect, useRef, useState } from 'react'
import { useInView } from '../lib/hooks/useInView'
import { PROJECT_EXPLORE_HANDOFF_EVENT } from '../lib/hubTools'

export const getMediaUrl = (path) => {
  if (!path) return null
  if (path.startsWith('http')) return path
  return `${process.env.PUBLIC_URL}${path}`
}

const PAUSE_FRAME = 0.1

const ProjectMedia = ({
  title,
  video,
  poster,
  aspectClassName = 'aspect-[4/3]',
  playOnHover = true,
  playWhenVisible = false,
  autoPlay = false,
  playFromStart = false,
  startTime,
}) => {
  const videoRef = useRef(null)
  const [frameReady, setFrameReady] = useState(false)
  const videoUrl = getMediaUrl(video)
  const posterUrl = getMediaUrl(poster)
  const hasPoster = Boolean(posterUrl)
  const shouldPlayOnHover = playOnHover && !autoPlay
  const { ref, isVisible } = useInView({}, !playWhenVisible)

  useEffect(() => {
    setFrameReady(false)
  }, [videoUrl])

  useEffect(() => {
    const element = videoRef.current
    if (!element || !videoUrl || !playWhenVisible || autoPlay) return undefined

    if (isVisible) {
      element.play().catch(() => {})
      return undefined
    }

    element.pause()
    return undefined
  }, [autoPlay, isVisible, playWhenVisible, videoUrl])

  useEffect(() => {
    const element = videoRef.current
    if (!element || !videoUrl) return undefined

    if (autoPlay) {
      const startPlayback = () => {
        setFrameReady(true)
        if (playFromStart) {
          try {
            element.currentTime = 0
          } catch {
            // Ignore seek errors on some codecs mid-load
          }
        }
        element.play().catch(() => {})
      }

      if (element.readyState >= 2) {
        startPlayback()
        return undefined
      }

      element.addEventListener('loadeddata', startPlayback)
      return () => element.removeEventListener('loadeddata', startPlayback)
    }

    const parkedTime = typeof startTime === 'number' ? startTime : hasPoster ? 0 : PAUSE_FRAME
    const parkFrame = () => {
      try {
        if (Math.abs((element.currentTime || 0) - parkedTime) > 0.04) {
          element.currentTime = parkedTime
        }
      } catch {
        // Ignore seek errors on some codecs mid-load
      }
      element.pause()
      setFrameReady(true)
    }

    if (element.readyState >= 2) {
      parkFrame()
      return undefined
    }

    element.addEventListener('loadeddata', parkFrame)
    return () => element.removeEventListener('loadeddata', parkFrame)
  }, [autoPlay, hasPoster, playFromStart, startTime, videoUrl])

  useEffect(() => {
    if (!playFromStart) return undefined

    const handleHandoff = () => {
      const element = videoRef.current
      if (!element || !videoUrl) return

      setFrameReady(true)
      try {
        element.currentTime = 0
      } catch {
        // Ignore seek errors on some codecs mid-load
      }
      element.play().catch(() => {})
    }

    window.addEventListener(PROJECT_EXPLORE_HANDOFF_EVENT, handleHandoff)
    return () => window.removeEventListener(PROJECT_EXPLORE_HANDOFF_EVENT, handleHandoff)
  }, [playFromStart, videoUrl])

  useEffect(() => {
    const element = videoRef.current
    if (!element || !videoUrl || !playWhenVisible || autoPlay) return undefined

    const showStillFrame = () => {
      if (hasPoster) {
        element.pause()
        setFrameReady(true)
        return
      }

      element.currentTime = PAUSE_FRAME
      element.pause()
      setFrameReady(true)
    }

    element.addEventListener('loadeddata', showStillFrame)
    return () => element.removeEventListener('loadeddata', showStillFrame)
  }, [autoPlay, hasPoster, playWhenVisible, videoUrl])

  const handleMouseEnter = () => {
    const element = videoRef.current
    if (!element || !videoUrl || !shouldPlayOnHover) return

    if (element.readyState < 2) {
      element.load()
    }

    element.play().catch(() => {})
  }

  const handleMouseLeave = () => {
    const element = videoRef.current
    if (!element || !videoUrl || !shouldPlayOnHover) return

    element.pause()
    element.currentTime = hasPoster ? 0 : PAUSE_FRAME
  }

  const getPreloadValue = () => {
    if (autoPlay) return 'auto'
    if (hasPoster) return 'none'
    return 'metadata'
  }

  return (
    <div
      ref={ref}
      className={`site-frame site-media-frame relative overflow-hidden transition-colors hover:[border-color:var(--color-accent-border)] group-hover:[border-color:var(--color-accent-border)] ${aspectClassName}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {videoUrl ? (
        <>
          {!hasPoster && !playFromStart ? (
            <div
              className={[
                'absolute inset-0 flex items-center justify-center bg-secondary transition-opacity duration-300',
                frameReady ? 'pointer-events-none opacity-0' : 'opacity-100',
              ].join(' ')}
              aria-hidden="true"
            />
          ) : null}
          <video
            ref={videoRef}
            className={[
              'h-full w-full object-cover transition-opacity duration-300',
              frameReady || hasPoster || playFromStart ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
            src={videoUrl}
            poster={posterUrl || undefined}
            muted
            loop
            playsInline
            preload={getPreloadValue()}
            autoPlay={autoPlay}
            aria-label={`${title} preview video`}
          />
        </>
      ) : (
        <div
          className="flex h-full w-full items-center justify-center bg-secondary"
          role="img"
          aria-label={`${title} preview placeholder`}
        >
          <span className="site-muted px-4 text-center normal-case">{title}</span>
        </div>
      )}
    </div>
  )
}

export default ProjectMedia
